import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import DataTable from "@/components/Datatable/Datatable";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import { useNotification } from "@/context/NotificationContext";
import { useAppDispatch } from "@/app/hooks";
import { useCourseList } from "@/modules/learningCenter/features/CourseManagement/hooks/useCourseList/useCourseList";
import { createCourseColumns } from "@/modules/learningCenter/features/CourseManagement/components/CourseColumns/CourseColumns";
import CourseFilters from "@/modules/learningCenter/features/CourseManagement/components/CourseFilters/CourseFilters";
import {
  publishCourseAction,
  archiveCourseAction,
} from "@/modules/learningCenter/features/CourseManagement/slices/courseListSlice";
import { CourseListItem } from "@/modules/learningCenter/features/CourseManagement/types/course";
import { buildArchiveWarning } from "@/modules/learningCenter/features/CourseManagement/components/CourseList/archiveWarning";

const CourseList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const {
    courses,
    totalCount,
    loading,
    error,
    metadata,
    pagination,
    sorting,
    filters,
    refetch,
    handleFilterChange,
    handlePaginationChange,
    handleSortingChange,
  } = useCourseList();

  const [courseToArchive, setCourseToArchive] = useState<CourseListItem | null>(
    null,
  );

  const handleCreateCourse = useCallback(() => {
    navigate("/admin/learning-center/courses/new");
  }, [navigate]);

  const handleEditCourse = useCallback(
    (uuid: string) => {
      navigate(`/admin/learning-center/courses/${uuid}/edit`);
    },
    [navigate],
  );

  const handlePublishCourse = useCallback(
    (course: CourseListItem) => {
      dispatch(
        publishCourseAction(course.uuid, () => {
          showNotification(
            "Course published",
            `"${course.title}" is now published.`,
            "success",
          );
          refetch();
        }),
      );
    },
    [dispatch, showNotification, refetch],
  );

  const handleArchiveCourse = useCallback((course: CourseListItem) => {
    setCourseToArchive(course);
  }, []);

  const handleConfirmArchive = useCallback(() => {
    if (!courseToArchive) {
      return;
    }
    const course = courseToArchive;
    dispatch(
      archiveCourseAction(course.uuid, () => {
        showNotification(
          "Course archived",
          `"${course.title}" has been archived.`,
          "success",
        );
        setCourseToArchive(null);
        refetch();
      }),
    );
  }, [courseToArchive, dispatch, showNotification, refetch]);

  const columns = useMemo(
    () =>
      createCourseColumns({
        onEditCourse: handleEditCourse,
        onPublishCourse: handlePublishCourse,
        onArchiveCourse: handleArchiveCourse,
      }),
    [handleEditCourse, handlePublishCourse, handleArchiveCourse],
  );

  const archiveWarning = buildArchiveWarning(
    courseToArchive?.totalEnrolled ?? 0,
  );

  return (
    <>
      <MainPageWrapper
        actions={<Button onClick={handleCreateCourse}>Create Course</Button>}
        error={error}
        subtitle="Create, edit, publish, and archive courses."
        title="Course Management"
      >
        <CourseFilters
          filters={filters}
          metadata={metadata}
          onFilterChange={handleFilterChange}
        />

        <DataTable<CourseListItem>
          columns={columns}
          data={courses}
          error={error}
          loading={loading}
          noDataMessage="No courses found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.uuid}
          sorting={sorting}
          totalCount={totalCount}
        />
      </MainPageWrapper>

      <ConfirmationDialog
        cancelMessage="Cancel"
        confirmMessage="Archive"
        dialogInstructionFinalQuestion="Are you sure you want to archive this course?"
        dialogInstructionItems={archiveWarning.items}
        dialogInstructionTitle={archiveWarning.title}
        dialogTitleContent={`Archive "${courseToArchive?.title ?? ""}"?`}
        isOpen={courseToArchive !== null}
        onClose={() => setCourseToArchive(null)}
        onConfirm={handleConfirmArchive}
      />
    </>
  );
};

export default CourseList;
